import type { Logger } from '@core/model/domain/logger';
import { poll } from '@core/model/framework/notifications/polling';
import { Bad } from '@core/model/framework/Result/BadResult';
import { Ok } from '@core/model/framework/Result/OkResult';
import {
  WaitOnBankAccountAnalysisCompletionGateway,
  WaitOnBankAccountAnalysisCompletionGatewayInput,
  WaitOnBankAccountAnalysisCompletionGatewayOutput,
} from '@core/model/gateway/waitOnBankAccountAnalysisCompletionGateway';
import type {
  AccountService,
  RequestAnalysisServiceInput,
  RequestAnalysisServiceOutput,
} from '@core/model/service/accountService';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';
import { BehaviorSubject, firstValueFrom, from, Observable, TimeoutError } from 'rxjs';

@injectable()
export class WaitOnBankAccountAnalysisCompletionViaPolling
  implements WaitOnBankAccountAnalysisCompletionGateway
{
  private manualForceStopPolling$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    @inject(SERVICE_ID.accountService) private readonly accountService: AccountService,
    @inject(SERVICE_ID.waitOnBankAccountAnalysisCompletionTimeoutInSeconds)
    private readonly timeoutInSeconds: number,
    @inject(SERVICE_ID.waitOnBankAccountAnalysisCompletionIntervalInSeconds)
    private readonly intervalInSeconds: number,
    @inject(SERVICE_ID.logger)
    private readonly logger: Logger
  ) {}

  async await(
    input: WaitOnBankAccountAnalysisCompletionGatewayInput
  ): WaitOnBankAccountAnalysisCompletionGatewayOutput {
    try {
      const result = await firstValueFrom(
        poll<WaitOnBankAccountAnalysisCompletionGatewayInput, Awaited<RequestAnalysisServiceOutput>>({
          intervalInSeconds: this.intervalInSeconds,
          timeoutInSeconds: this.timeoutInSeconds,
          apiCall: () => this.getRequestAnalysisAsObservable(input),
          shouldContinuePolling: (bankConnection) => this.shouldContinuePolling(bankConnection),
          manualForceStop: this.manualForceStopPolling$,
          logger: this.logger,
          pollingName: 'BANK_ACCOUNT_ANALYSIS_COMPLETION',
        })
      );

      if (result.success === false && result.reason === 'CASE_IN_INCORRECT_STATUS') {
        return Bad('CASE_IN_INCORRECT_STATUS');
      }

      return Ok();
    } catch (e) {
      if (e instanceof TimeoutError) {
        return Bad('WAITED_SYNCHRONIZATION_COMPLETION_FOR_TOO_LONG');
      } else if (e === 'STOPPED_POLLING_VIA_USER_ACTION') {
        this.manualForceStopPolling$.next(false);
        return Bad('STOPPED_POLLING_VIA_USER_ACTION');
      }

      this.logger.logException(e);
      throw e;
    }
  }

  stop() {
    this.manualForceStopPolling$.next(true);
  }

  private getRequestAnalysisAsObservable(
    input: RequestAnalysisServiceInput
  ): Observable<Awaited<RequestAnalysisServiceOutput>> {
    return new Observable((observer) => {
      from(this.accountService.requestAnalysis(input)).subscribe({
        next: (result) => {
          observer.next(result);
        },
      });
    });
  }

  private shouldContinuePolling(requestAnalysisResult: Awaited<RequestAnalysisServiceOutput>): boolean {
    if (
      requestAnalysisResult.success === false &&
      requestAnalysisResult.reason === 'SOME_BANK_ACCOUNTS_ARE_NOT_SYNCHRONIZED_YET'
    ) {
      return true;
    }

    return false;
  }
}
